/**
 * 이상한 방 - 카탈로그 엔트리: 식기·음식 (Kenney Food Kit 변환분)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4.1절(그리드 해상도) · 4.2절(무변화 회전) · 8절(카탈로그 규격 요건)
 * 계약: `../entryTypes.ts` · 실측: `public/assets/models.json` · 시각 색인: `public/assets/catalog-preview.png`
 *
 * ## 이 그룹이 맡는 일 - 밀도
 *
 * 391개 배치에서 방을 "꽉 차 보이게" 만드는 건 대형 가구가 아니라 상판 위 소품이다.
 * 가구는 구획당 서너 개면 끝이지만 소품은 책상·식탁·선반마다 얹히므로 개수가 붙는다.
 * 그래서 여기 45종은 거의 전부 `sizeClass: 'surface'`이고 footprint가 1×1×1이다.
 *
 * 그 1×1×1이 그냥 "작다"가 아니라 **그리드 해상도의 정의 근거**라는 점이 중요하다(4.1절).
 * `mug`(9.6 × 7.7 × 8.0 cm)가 정확히 한 칸이고, 이 그룹의 최소 소품인 `shaker-salt`(2.7cm)도
 * `cellsFromMeters`의 "각 축 최소 1" 규칙에 따라 한 칸을 차지한다. 셀보다 작은 소품을 허용하는 순간
 * 한 칸 이동이 소품 하나보다 큰 어긋남이 되어 4.1절의 근거가 무너지므로, 이 하한은 타협하지 않는다.
 *
 * ## footprint는 손으로 적지 않는다
 *
 * 각 엔트리의 두 번째 인자는 `models.json`의 `sizeMeters`를 **그대로 옮긴 값**이고,
 * 셀 변환은 전부 `cellsFromMeters()`가 한다. 눈대중으로 셀 수를 적으면 반드시 어긋나고,
 * 어긋난 결과는 "눕혀진 접시"가 아니라 **점유 셀이 틀린 판정 오류**다.
 * 매니페스트 값을 여기 복제한 이유는 `models.json`이 `public/` 자산이라 소스가 import할 수 없기 때문이다
 * (`entryTypes.ts`의 `knownModelIds` 주석과 같은 이유). 대조는 테스트가 매니페스트를 읽어서 한다.
 *
 * ## recolorSlot - 원칙은 slot0, 예외는 눈으로 확인한 것만
 *
 * 변환된 Food Kit은 의미명이 없고 `slot0`이 **면적상 지배적인 색**이다(ASSETS.md 4절).
 * 그래서 기본값은 slot0이다. 다만 면적 계산은 화면에 보이지 않는 안쪽 면까지 세므로,
 * 미리보기에서 slot0 색이 실제로 안 보이는 세 종(`glass-wine`·`can-small`·`watermelon`)과
 * 몸통이 따로 있는 `rolling-pin`만 slot1로 옮겼다. 안 보이는 면을 칠하면 서버 diff에는 잡히는데
 * 화면에는 아무 일도 안 일어나는 변화가 되고, 그건 난이도가 아니라 원칙 2 위반이다.
 *
 * ## rotSymmetry - 애매하면 큰 쪽(4·2)으로 잠근다
 *
 * 45종 중 25종이 `4`다. 컵·접시·볼·캔·과일이 죄다 축 대칭이라 그렇고, 이건 손실이 아니라
 * 체인저의 잔머리 재료다(4절: "회전이 티 안 나는 소품이 섞여 있는 것"). 판정이 애매한 것
 * - 초코칩이 흩어진 `cookie`, 꼭지가 달린 `apple` - 도 4로 잠갔다. 방향을 **과소** 선언하면
 * 쓸 수 있는 동사 하나를 잃을 뿐이지만, **과대** 선언하면 찾을 수 없는 정답이 생기기 때문이다.
 *
 * ## 채택하지 않은 모델 (담당 56종 중 11종)
 *
 * | 모델 | 이유 |
 * |---|---|
 * | `mug` · `cup` · `plate` | 기존 44종이 이미 같은 id로 배선돼 있다. modelId가 겹치면 실루엣 중복이다 |
 * | `cup-tea` | `cup`과 같은 메시(치수 동일). 차이는 안에 담긴 차 색뿐이다 |
 * | `cup-saucer` | `plate`와 같은 납작 원판. 지름만 작아 한 칸 안에서는 구별되지 않는다 |
 * | `bowl-soup` | `bowl`과 sizeMeters가 완전히 같다. 내용물만 다른 같은 그릇이다 |
 * | `bottle-musterd` | `bottle-ketchup`과 치수가 동일한 같은 스퀴즈 병. 색만 다르다 |
 * | `shaker-pepper` | `shaker-salt`와 같은 메시. 색만 다르다 |
 * | `soda-can` | `can`과 같은 원통 실루엣. 한 칸 크기에서 풀탭은 읽히지 않는다 |
 * | `carton-small` | `carton`과 같은 게이블탑 팩을 높이만 줄인 것 |
 * | `utensil-knife` | `utensil-fork`와 같은 은색 막대. 한 상판에 셋을 깔면 난이도가 아니라 짜증이다 |
 *
 * 억지로 종수를 채우는 대신 실루엣이 실제로 갈리는 45종만 남겼다(8절).
 */

import type { CatalogEntryDraft } from '../entryTypes'
import { cellsFromMeters } from '../entryTypes'

/**
 * 팔레트 인덱스에 이름을 붙인 것. `palette.ts`의 `PALETTE` 배열 순서가 계약이므로 값 자체는 그 사본이다.
 * 숫자를 그대로 적으면 `defaultColorId: 9`가 무슨 색인지 알 수 없어 검토가 불가능해진다.
 */
const COLOR = {
  cocoa: 0,
  plum: 1,
  inkTeal: 2,
  deepSage: 3,
  lilac: 4,
  latte: 5,
  sage: 6,
  rose: 7,
  apricotCream: 8,
  milk: 9,
} as const

/**
 * 배치 빈도 사다리(`weight`, 1~10).
 *
 * 균등 분포로 뽑으면 아파트에 절구가 아홉 개 놓인다. 밀도는 종 수가 아니라 이 분포에서 나오므로
 * 다섯 단계로 눈금을 고정해 두고, 엔트리마다 "이 집에 몇 개나 있을 물건인가"만 판단한다.
 */
const FREQ = {
  /** 한 집에 있을까 말까 (수박·절구·피자박스) */
  odd: 2,
  /** 가끔 (와인잔·꿀단지·거품기) */
  occasional: 3,
  /** 보통 (냄비·크루아상·후추그라인더) */
  normal: 4,
  /** 흔함 (접시·통조림·사과) */
  common: 5,
  /** 주방에 반드시 있는 것 (컵·볼·수저) */
  staple: 7,
} as const

/** `entry()`가 스스로 채우는 세 필드를 뺀 나머지. 작성자가 실제로 판단해야 하는 값만 남는다. */
type EntryBody = Omit<CatalogEntryDraft, 'id' | 'modelId' | 'footprint'>

/**
 * 엔트리 하나를 만든다. **footprint를 손으로 적을 방법이 없게 만드는 것**이 이 함수의 존재 이유다.
 *
 * `id`를 `modelId`와 같게 두는 것도 의도적이다 - 이 그룹은 45종 전부가 GLB 하나에 1:1로 대응하므로
 * 이름을 따로 지으면 "카탈로그의 그 컵이 어느 파일이었나"를 매번 되짚어야 한다.
 */
function entry(
  modelId: string,
  sizeMeters: Readonly<{ x: number; y: number; z: number }>,
  body: EntryBody,
): CatalogEntryDraft {
  return { id: modelId, modelId, footprint: cellsFromMeters(sizeMeters), ...body }
}

/**
 * 식기·음식 45종. `sizeClass`는 `barrel`·`bag`·`pizza-box` 셋만 `floor`이고 나머지는 전부 `surface`다.
 *
 * rotSymmetry 분포: 4가 25종 / 2가 8종 / 1이 12종.
 * footprint는 `frying-pan`(1×1×2)과 `pizza-box`(2×1×2)를 뺀 43종이 1×1×1이다 -
 * 4.1절이 요구하는 최소 소품 구간을 이 그룹이 통째로 담당한다.
 */
export const TABLEWARE_FOOD_ENTRIES: readonly CatalogEntryDraft[] = [
  /* --- 식기 - 잔·접시·볼. 대부분 원형이라 rotSymmetry 4가 많다(회전 동사가 잠긴다) --- */
  // 손잡이가 방향을 만든다 → 네 방향이 전부 구별된다. slot0(흰 잔몸)이 주 색면.
  entry('cup-coffee', { x: 0.061, y: 0.039, z: 0.08 }, {
    name: '커피잔', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living', 'bedroom', 'hall'], weight: FREQ.staple, defaultColorId: COLOR.milk,
  }),
  // 손잡이 없는 원통 → 돌려도 화면에 아무 일도 없다. 욕실 양치컵으로도 선다.
  entry('glass', { x: 0.045, y: 0.08, z: 0.052 }, {
    name: '유리컵', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living', 'bedroom', 'bath', 'hall'], weight: FREQ.staple, defaultColorId: COLOR.milk,
  }),
  // 축 대칭 원형. slot0(분홍)은 잔 안쪽 와인이라 밖에서 안 보여 slot1(잔몸)을 칠한다.
  entry('glass-wine', { x: 0.063, y: 0.14, z: 0.055 }, {
    name: '와인잔', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot1',
    tags: ['kitchen', 'living'], weight: FREQ.normal, defaultColorId: COLOR.milk,
  }),
  // 원형 접시 + 가운데 솟은 음식 더미. 원형이므로 회전은 무변화다.
  entry('plate-deep', { x: 0.25, y: 0.041, z: 0.25 }, {
    name: '오목접시', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.common, defaultColorId: COLOR.milk,
  }),
  // 기존 plate보다 두껍고 음식이 올라가 실루엣이 갈린다. 원형이라 rotSymmetry 4.
  entry('plate-dinner', { x: 0.25, y: 0.062, z: 0.25 }, {
    name: '정식접시', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living'], weight: FREQ.common, defaultColorId: COLOR.milk,
  }),
  // 얕은 육각 볼 → 축 대칭. slot0이 바깥면이라 칠하면 그대로 보인다.
  entry('bowl', { x: 0.141, y: 0.06, z: 0.162 }, {
    name: '볼', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living'], weight: FREQ.staple, defaultColorId: COLOR.milk,
  }),
  // bowl의 두 배 높이라 깊은 그릇으로 읽힌다. 축 대칭이라 회전은 잠근다.
  entry('bowl-cereal', { x: 0.141, y: 0.117, z: 0.178 }, {
    name: '시리얼볼', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.normal, defaultColorId: COLOR.inkTeal,
  }),

  /* --- 조리도구 - 손잡이 유무가 rotSymmetry를 가른다 --- */
  // 양쪽에 같은 손잡이 → 180°는 무변화, 90°는 손잡이 축이 돌아 보인다.
  entry('pot', { x: 0.184, y: 0.101, z: 0.23 }, {
    name: '냄비', rotSymmetry: 2, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.common, defaultColorId: COLOR.inkTeal,
  }),
  // 원판 위 일자 손잡이. 손잡이가 축이라 180° 대칭이다.
  entry('pot-lid', { x: 0.184, y: 0.033, z: 0.175 }, {
    name: '냄비뚜껑', rotSymmetry: 2, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.occasional, defaultColorId: COLOR.inkTeal,
  }),
  // 넓고 얕은 양수 팬 → pot과 높이로 갈리고 대칭은 같다.
  entry('pan', { x: 0.184, y: 0.049, z: 0.213 }, {
    name: '팬', rotSymmetry: 2, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.normal, defaultColorId: COLOR.inkTeal,
  }),
  // 한쪽으로만 긴 손잡이 → 네 방향 전부 구별. 이 그룹에서 유일하게 z가 2셀이다.
  entry('frying-pan', { x: 0.184, y: 0.034, z: 0.286 }, {
    name: '프라이팬', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.common, defaultColorId: COLOR.inkTeal,
  }),
  // 직사각 판 → 90°는 가로세로가 바뀌어 확연하고 180°는 무변화다.
  entry('cutting-board', { x: 0.162, y: 0.017, z: 0.23 }, {
    name: '도마', rotSymmetry: 2, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.common, defaultColorId: COLOR.latte,
  }),
  // 비스듬히 깎인 블록에 칼이 꽂혀 있다 → 기울기가 방향을 드러낸다.
  entry('knife-block', { x: 0.178, y: 0.185, z: 0.062 }, {
    name: '칼블록', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.occasional, defaultColorId: COLOR.latte,
  }),
  // 한쪽 끝에만 갈래가 있다 → 비대칭. 상판을 채우는 최소 소품.
  entry('utensil-fork', { x: 0.141, y: 0.005, z: 0.024 }, {
    name: '포크', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living'], weight: FREQ.staple, defaultColorId: COLOR.milk,
  }),
  // 한쪽 끝의 오목한 머리가 방향을 만든다. 포크와 머리 모양이 확실히 다르다.
  entry('utensil-spoon', { x: 0.134, y: 0.008, z: 0.034 }, {
    name: '숟가락', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living'], weight: FREQ.staple, defaultColorId: COLOR.milk,
  }),
  // 손잡이 쪽으로만 길다 → 비대칭. slot0이 철사 부분이라 색이 크게 먹는다.
  entry('whisk', { x: 0.203, y: 0.044, z: 0.044 }, {
    name: '거품기', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.occasional, defaultColorId: COLOR.milk,
  }),
  // 두꺼운 원형 그릇 → 회전 무변화. 머티리얼이 하나뿐이라 선택지가 slot0밖에 없다.
  entry('mortar', { x: 0.111, y: 0.059, z: 0.128 }, {
    name: '절구', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.odd, defaultColorId: COLOR.cocoa,
  }),
  // 양끝 손잡이가 같다 → 180° 무변화. slot1이 가운데 몸통(가장 넓은 색면)이다.
  entry('rolling-pin', { x: 0.228, y: 0.039, z: 0.045 }, {
    name: '밀대', rotSymmetry: 2, sizeClass: 'surface', recolorSlot: 'slot1',
    tags: ['kitchen'], weight: FREQ.occasional, defaultColorId: COLOR.latte,
  }),
  // 받침 달린 원형 컵 → 축 대칭. 4~5cm라 상판 틈을 메우는 용도다.
  entry('egg-cup', { x: 0.044, y: 0.042, z: 0.051 }, {
    name: '에그컵', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.odd, defaultColorId: COLOR.milk,
  }),
  // 원형 컵 + 돔 → 회전 무변화. slot0은 종이컵 밝은 면이라 칠하면 눈에 띈다.
  entry('cupcake', { x: 0.099, y: 0.119, z: 0.086 }, {
    name: '컵케이크', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living'], weight: FREQ.occasional, defaultColorId: COLOR.lilac,
  }),

  /* --- 용기 - 병·캔·통. 거의 전부 원통이라 rotSymmetry 4다 --- */
  // 가늘고 긴 원통 → 축 대칭. 높이 17cm로 병 계열에서 가장 크다.
  entry('bottle-oil', { x: 0.039, y: 0.174, z: 0.039 }, {
    name: '기름병', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.common, defaultColorId: COLOR.deepSage,
  }),
  // 스퀴즈 병. 머티리얼이 하나라 전체가 한 색으로 바뀐다.
  entry('bottle-ketchup', { x: 0.04, y: 0.11, z: 0.047 }, {
    name: '케첩병', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.normal, defaultColorId: COLOR.plum,
  }),
  // 원통 → 회전 무변화. slot0이 몸통 라벨이라 색 변경이 가장 크게 보인다.
  entry('can', { x: 0.084, y: 0.09, z: 0.084 }, {
    name: '통조림', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'hall'], weight: FREQ.common, defaultColorId: COLOR.plum,
  }),
  // can과 지름이 같고 높이가 절반이라 실루엣이 갈린다. slot1이 몸통, slot0은 뚜껑이다.
  entry('can-small', { x: 0.084, y: 0.045, z: 0.084 }, {
    name: '납작 통조림', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot1',
    tags: ['kitchen'], weight: FREQ.normal, defaultColorId: COLOR.lilac,
  }),
  // 목이 좁은 원통 → 축 대칭. 침대 옆·복도 어디에 굴러다녀도 자연스럽다.
  entry('soda-bottle', { x: 0.051, y: 0.163, z: 0.044 }, {
    name: '음료병', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living', 'bedroom', 'hall'], weight: FREQ.normal, defaultColorId: COLOR.cocoa,
  }),
  // 한쪽 모서리만 열린 게이블탑 → 네 방향이 전부 구별된다.
  entry('carton', { x: 0.064, y: 0.165, z: 0.064 }, {
    name: '우유팩', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'hall'], weight: FREQ.normal, defaultColorId: COLOR.lilac,
  }),
  // 눕힌 원통이라 긴 축이 있다 → 180° 무변화. 바닥에 두는 몇 안 되는 소품.
  entry('barrel', { x: 0.213, y: 0.19, z: 0.19 }, {
    name: '나무통', rotSymmetry: 2, sizeClass: 'floor', recolorSlot: 'slot0',
    tags: ['kitchen', 'hall'], weight: FREQ.odd, defaultColorId: COLOR.cocoa,
  }),
  // 윗단을 일자로 접은 봉투 → 접힌 선이 축이라 180° 대칭. 바닥에 세운다.
  entry('bag', { x: 0.062, y: 0.168, z: 0.115 }, {
    name: '종이봉투', rotSymmetry: 2, sizeClass: 'floor', recolorSlot: 'slot0',
    tags: ['kitchen', 'hall'], weight: FREQ.normal, defaultColorId: COLOR.latte,
  }),
  // 작은 원통 → 회전 무변화. 5.5cm로 이 그룹에서 가장 작다.
  entry('shaker-salt', { x: 0.027, y: 0.055, z: 0.027 }, {
    name: '소금통', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.common, defaultColorId: COLOR.milk,
  }),
  // 선반으로 깎은 축 대칭 형상. 높이 15cm라 소금통과 실루엣이 확실히 갈린다.
  entry('pepper-mill', { x: 0.044, y: 0.153, z: 0.044 }, {
    name: '후추그라인더', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.normal, defaultColorId: COLOR.latte,
  }),
  // 배가 부른 원형 항아리 → 축 대칭.
  entry('honey', { x: 0.091, y: 0.088, z: 0.091 }, {
    name: '꿀단지', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.occasional, defaultColorId: COLOR.latte,
  }),
  // 원형 병 + 가운데 라벨 띠. slot0이 그 라벨이라 색이 띠로 들어간다.
  entry('peanut-butter', { x: 0.066, y: 0.083, z: 0.066 }, {
    name: '땅콩버터', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.occasional, defaultColorId: COLOR.deepSage,
  }),

  /* --- 음식 - 대부분 kitchen이지만 간식류는 living/hall까지 벌려 밀도를 번다 --- */
  // 구형. 꼭지와 잎이 있지만 5cm짜리에서 그 회전은 찾을 수 없다 → 4로 잠근다.
  entry('apple', { x: 0.055, y: 0.053, z: 0.055 }, {
    name: '사과', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living', 'bedroom', 'hall'], weight: FREQ.common, defaultColorId: COLOR.rose,
  }),
  // 휜 방향이 그대로 드러난다 → 네 방향 전부 구별.
  entry('banana', { x: 0.039, y: 0.055, z: 0.176 }, {
    name: '바나나', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living', 'hall'], weight: FREQ.common, defaultColorId: COLOR.apricotCream,
  }),
  // 한쪽만 둥근 슬라이스 → 위에서 보면 앞뒤가 다르다. 두께 1cm의 납작 소품.
  entry('bread', { x: 0.124, y: 0.011, z: 0.102 }, {
    name: '식빵', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.common, defaultColorId: COLOR.apricotCream,
  }),
  // 양끝이 같은 반원기둥 → 180° 무변화. 식빵과 부피가 완전히 다르다.
  entry('loaf', { x: 0.162, y: 0.124, z: 0.102 }, {
    name: '빵덩이', rotSymmetry: 2, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.normal, defaultColorId: COLOR.latte,
  }),
  // 원형 층 케이크 → 회전 무변화. 특이 소품이라 가중치를 낮게 둔다.
  entry('cake', { x: 0.179, y: 0.076, z: 0.179 }, {
    name: '케이크', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living'], weight: FREQ.odd, defaultColorId: COLOR.rose,
  }),
  // 원판. 초코칩이 있지만 6cm에서 그 배치 회전은 식별 불가라 4로 잠근다.
  entry('cookie', { x: 0.062, y: 0.014, z: 0.053 }, {
    name: '쿠키', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living', 'bedroom', 'hall'], weight: FREQ.common, defaultColorId: COLOR.latte,
  }),
  // 초승달 → 어느 각도든 다르게 보인다.
  entry('croissant', { x: 0.097, y: 0.054, z: 0.134 }, {
    name: '크루아상', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living'], weight: FREQ.normal, defaultColorId: COLOR.latte,
  }),
  // 링 → 완전 대칭.
  entry('donut', { x: 0.069, y: 0.025, z: 0.079 }, {
    name: '도넛', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living', 'hall'], weight: FREQ.normal, defaultColorId: COLOR.latte,
  }),
  // 원형 컵 없는 돔 → 축 대칭. 컵케이크와 높이·실루엣이 갈린다.
  entry('muffin', { x: 0.085, y: 0.07, z: 0.074 }, {
    name: '머핀', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living', 'hall'], weight: FREQ.normal, defaultColorId: COLOR.latte,
  }),
  // 삼각형 → 90°·180° 모두 보인다. slot0(초록 채소 띠)이 옆면에 선명하게 드러난다.
  entry('sandwich', { x: 0.121, y: 0.047, z: 0.121 }, {
    name: '샌드위치', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living'], weight: FREQ.normal, defaultColorId: COLOR.deepSage,
  }),
  // 뚜껑이 한쪽으로 열려 있다 → 완전 비대칭. 이 그룹에서 유일한 2×2 바닥 소품이다.
  entry('pizza-box', { x: 0.264, y: 0.247, z: 0.267 }, {
    name: '피자박스', rotSymmetry: 1, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen', 'living', 'hall'], weight: FREQ.odd, defaultColorId: COLOR.apricotCream,
  }),
  // 골이 균등한 원형 → 회전 무변화.
  entry('pumpkin', { x: 0.117, y: 0.064, z: 0.117 }, {
    name: '호박', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot0',
    tags: ['kitchen'], weight: FREQ.odd, defaultColorId: COLOR.latte,
  }),
  // 구형이라 회전 무변화. slot0(빨강)은 속살이라 화면에 없고 slot1(초록 껍질)이 주 색면이다.
  entry('watermelon', { x: 0.128, y: 0.133, z: 0.128 }, {
    name: '수박', rotSymmetry: 4, sizeClass: 'surface', recolorSlot: 'slot1',
    tags: ['kitchen'], weight: FREQ.odd, defaultColorId: COLOR.deepSage,
  }),
]
